import { useState } from 'react';

interface FAQItem {
  question: string;
  answer: string;
}

const faqData: FAQItem[] = [
  {
    question: 'Quels services de transporteur express propose Transporteur Express IDF en Île-de-France ?',
    answer: 'Transporteur Express IDF est un transporteur express basé en Île-de-France spécialisé dans la livraison urgente de colis, plis et marchandises. Notre service de coursier intervient 24h/24 et 7j/7 pour assurer un transport rapide, sécurisé et direct sans rupture de charge.'
  },
  {
    question: 'Comment envoyer un colis urgent avec un coursier en Île-de-France ?',
    answer: 'Pour envoyer un colis urgent, contactez Transporteur Express IDF, votre transporteur express en Île-de-France. Notre coursier récupère votre colis rapidement et assure une livraison immédiate à Paris et dans toute la région.'
  },
  {
    question: 'Quels sont les tarifs d\'un transporteur express en Île-de-France ?',
    answer: 'Les tarifs d\'un transporteur ou d\'un coursier express varient selon la distance, l\'urgence et le volume. Transporteur Express IDF propose des prix compétitifs en Île-de-France avec devis rapide et transparent.'
  },
  {
    question: 'Transport de documents confidentiels par coursier',
    answer: 'Notre service de transporteur express assure la livraison sécurisée de documents confidentiels en Île-de-France. Chaque coursier garantit une remise en main propre et un suivi précis.'
  },
  {
    question: 'Pourquoi choisir un transporteur local en Île-de-France ?',
    answer: 'Un transporteur local comme Transporteur Express IDF connaît parfaitement la région Île-de-France. Cela permet à notre service de coursier d\'optimiser les trajets et de réduire les délais de livraison.'
  },
  {
    question: 'Livraison urgente Paris et région',
    answer: 'Notre transporteur express intervient à Paris et dans toute l\'Île-de-France pour des livraisons urgentes le jour même. Notre coursier prend en charge vos colis immédiatement après validation.'
  }
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Generate Schema.org FAQPage structured data
  const generateSchema = () => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqData.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer
        }
      }))
    };

    return (
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
    );
  };

  return (
    <>
      {generateSchema()}
      <section id="faq" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-red-50 border border-red-100 rounded-full px-5 py-2 mb-6">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-question-line text-red-600 text-lg"></i>
              </div>
              <span className="text-sm font-semibold text-red-600">Questions Fréquentes</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Tout savoir sur notre service de <strong>transporteur express</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Vous avez des questions sur notre service de <strong>coursier Île-de-France</strong> ou nos <strong>livraisons urgentes Paris</strong> ? Trouvez toutes les réponses ici.
            </p>
          </div>

          {/* FAQ Accordion */}
          <div className="space-y-4">
            {faqData.map((item, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-2xl border-2 border-gray-100 overflow-hidden transition-all duration-300 hover:border-red-200"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full flex items-center justify-between p-6 text-left cursor-pointer group"
                  aria-expanded={openIndex === index}
                >
                  <div className="flex items-start gap-4 flex-1">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-xl transition-all duration-300 flex-shrink-0 ${
                      openIndex === index
                        ? 'bg-red-600 text-white'
                        : 'bg-white text-red-600 group-hover:bg-red-50'
                    }`}>
                      <i className={`ri-${openIndex === index ? 'subtract' : 'add'}-line text-xl`}></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 pr-4 leading-relaxed">
                      {item.question}
                    </h3>
                  </div>
                  <div className={`w-6 h-6 flex items-center justify-center transition-transform duration-300 flex-shrink-0 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}>
                    <i className="ri-arrow-down-s-line text-2xl text-gray-400"></i>
                  </div>
                </button>
                
                <div
                  className={`transition-all duration-300 ease-in-out ${
                    openIndex === index
                      ? 'max-h-96 opacity-100'
                      : 'max-h-0 opacity-0'
                  }`}
                  style={{ overflow: 'hidden' }}
                >
                  <div className="px-6 pb-6 pl-20">
                    <p className="text-gray-700 leading-relaxed">
                      {item.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-12 text-center bg-gradient-to-br from-red-50 to-yellow-50 rounded-3xl p-8 border border-red-100">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-red-600 rounded-2xl">
              <i className="ri-customer-service-2-line text-3xl text-white"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              Une autre question ?
            </h3>
            <p className="text-gray-600 mb-6 max-w-xl mx-auto">
              Notre équipe <strong>Transporteur express</strong> est disponible 24h/24 pour répondre à toutes vos questions sur nos services de <strong>transporteur express</strong> et <strong>coursier Île-de-France</strong>.
            </p>
            <a
              href="tel:+33632410246"
              className="inline-flex items-center gap-2 bg-red-600 text-white px-8 py-4 rounded-xl font-bold text-base hover:bg-red-700 transition-all shadow-lg whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line text-xl"></i>
              Appelez-nous maintenant
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
